import { PLUGIN_KINDS } from '@crowdex/agent-contracts/api';
import { z } from 'zod';

/**
 * What a plugin built from a `crowdex.json` brings beyond what every plugin has: how it was
 * installed, what it may do, its page and what it declares. The inventory carries it for the
 * Plugins page only: its skills, MCP servers, agents and connections reach agents by their own
 * paths (code mode, `/api/plugins/skills`, the hub), never through the plugin's other lists.
 */
export const crowdexPluginDetailsSchema = z.object({
  /** What it is to the user, derived from its manifest (`pluginKind`). */
  kind: z.enum(PLUGIN_KINDS).optional(),
  /** `provisioned`: this server provides it; `account`: a package of the account; `local`: a folder. */
  install: z.enum(['provisioned', 'account', 'local']),
  permissions: z.array(z.string()),
  /** What installing it granted, as the approval listed it. */
  grants: z.array(z.string()),
  /** Why it is off though turned on (an organization's policy). */
  blocked: z.string().optional(),
  /** A newer build waiting for the user to approve what it adds. */
  pending: z
    .object({
      digest: z.string(),
      version: z.string(),
      added: z.array(z.string()),
      grants: z.array(z.string()),
    })
    .optional(),
  /** The folder a `local` plugin is built from. */
  localPath: z.string().optional(),
  /** Its page: it is an app of All apps while it is enabled. */
  page: z.object({ title: z.string() }).optional(),
  /** The Crowlingo application it brings (`service` in `crowdex.json`): shown with its origin. */
  service: z.string().optional(),
  /** The core contracts it implements (`inference.v1`, `forge.v1`…). */
  provides: z.array(z.object({ contract: z.string(), title: z.string() })),
  mcp: z.array(z.object({ id: z.string(), title: z.string() })),
  skills: z.array(z.object({ name: z.string(), description: z.string() })),
  agents: z.array(z.object({ id: z.string(), name: z.string() })),
  /** Its VS Code color themes, offered in Settings › Appearance. */
  themes: z
    .array(z.object({ id: z.string(), label: z.string(), type: z.enum(['light', 'dark']) }))
    .optional(),
  actions: z.array(z.object({ id: z.string(), label: z.string() })),
  types: z.array(z.object({ type: z.string(), title: z.string() })),
  /**
   * On a machine without a sandbox for commands (Windows): each command, what it runs, and
   * whether the user let it run there without one. Absent where commands are sandboxed.
   */
  unsandboxedCommands: z
    .array(
      z.object({ id: z.string(), title: z.string(), program: z.string(), allowed: z.boolean() }),
    )
    .optional(),
});
export type CrowdexPluginDetails = z.infer<typeof crowdexPluginDetailsSchema>;

/** A `crowdex.json` folder of a machine, before the user approves what installing it grants. */
export type LocalPluginPreview = { name: string; title: string; version: string; grants: string[] };

/** Whether `local.install` answered a preview to approve rather than an installed plugin. */
export function isLocalPluginPreview(value: object): value is LocalPluginPreview {
  return !('installPath' in value) && 'grants' in value;
}
